import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { API_BASE } from '../config';

export interface AuthUser {
  email: string;
  displayName: string;
  pictureUrl?: string;
}

export interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginSocial: (provider: 'google' | 'apple', code: string) => Promise<void>;
  register: (email: string, password: string, displayName: string, referralCode?: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
  /** Fetch wrapper that automatically attaches the Authorization header */
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Decode a JWT payload without verification (client-side claims extraction only). */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1];
    if (!base64) return null;
    const payload = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Store refresh token and user info in localStorage for persistence
  const saveRefreshToken = useCallback((refreshToken: string | null) => {
    if (refreshToken) {
      localStorage.setItem('ws_refresh_token', refreshToken);
    } else {
      localStorage.removeItem('ws_refresh_token');
    }
  }, []);

  const saveUserInfo = useCallback((userInfo: AuthUser | null) => {
    if (userInfo) {
      localStorage.setItem('ws_user', JSON.stringify(userInfo));
    } else {
      localStorage.removeItem('ws_user');
    }
  }, []);

  const getRefreshToken = useCallback((): string | null => {
    return localStorage.getItem('ws_refresh_token');
  }, []);

  const getSavedUser = useCallback((): AuthUser | null => {
    try {
      const stored = localStorage.getItem('ws_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  // Try to refresh on mount
  useEffect(() => {
    refresh().catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch(`${API_BASE}/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });

    const json = await res.json();

    if (!res.ok) {
      throw new Error(json.error?.message || 'Invalid credentials');
    }

    setToken(json.data.accessToken);
    setUser({ email: json.data.email, displayName: json.data.displayName });
    saveRefreshToken(json.data.refreshToken);
    saveUserInfo({ email: json.data.email, displayName: json.data.displayName });
  }, [saveRefreshToken, saveUserInfo]);

  const loginSocial = useCallback(async (provider: 'google' | 'apple', code: string) => {
    const redirectUri = `${window.location.origin}/auth/callback`;
    const res = await fetch(`${API_BASE}/v1/auth/social`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ provider: provider === 'google' ? 'Google' : 'Apple', code, redirectUri }),
    });

    const json = await res.json();

    if (!res.ok) {
      throw new Error(json.error?.message || 'Social login failed');
    }

    // Extract profile picture from ID token claims
    let pictureUrl: string | undefined;
    let displayName = '';
    let email = '';
    if (json.data.idToken) {
      const claims = decodeJwtPayload(json.data.idToken);
      pictureUrl = claims?.picture as string | undefined;
      displayName = (claims?.name as string) || (claims?.email as string) || '';
      email = (claims?.email as string) || '';
    }

    const userInfo: AuthUser = {
      email: email || json.data.email || '',
      displayName: displayName || json.data.displayName || '',
      pictureUrl,
    };

    setToken(json.data.accessToken);
    setUser(userInfo);
    saveRefreshToken(json.data.refreshToken);
    saveUserInfo(userInfo);
  }, [saveRefreshToken, saveUserInfo]);

  const register = useCallback(async (email: string, password: string, displayName: string, referralCode?: string) => {
    const res = await fetch(`${API_BASE}/v1/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, displayName, ...(referralCode ? { referralCode } : {}) }),
    });

    const json = await res.json();

    if (!res.ok) {
      throw new Error(json.error?.message || 'Registration failed');
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    saveRefreshToken(null);
    saveUserInfo(null);
  }, [saveRefreshToken, saveUserInfo]);

  const refresh = useCallback(async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      setToken(null);
      setUser(null);
      return;
    }

    const res = await fetch(`${API_BASE}/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) {
      setToken(null);
      setUser(null);
      saveRefreshToken(null);
      saveUserInfo(null);
      return;
    }

    const json = await res.json();
    if (json.data?.accessToken) {
      setToken(json.data.accessToken);
      // Restore user info from localStorage (refresh endpoint doesn't return it)
      const savedUser = getSavedUser();
      if (savedUser) {
        setUser(savedUser);
      }
      // Update refresh token if a new one is provided
      if (json.data.refreshToken) {
        saveRefreshToken(json.data.refreshToken);
      }
    } else {
      setToken(null);
      setUser(null);
      saveRefreshToken(null);
      saveUserInfo(null);
    }
  }, [getRefreshToken, getSavedUser, saveRefreshToken, saveUserInfo]);

  const authFetch = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const headers = new Headers(options.headers);
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return fetch(url, { ...options, headers, credentials: 'include' });
    },
    [token]
  );

  const isAuthenticated = user !== null;

  const value = useMemo<AuthContextValue>(
    () => ({ user, token, isAuthenticated, loading, login, loginSocial, register, logout, refresh, authFetch }),
    [user, token, isAuthenticated, loading, login, loginSocial, register, logout, refresh, authFetch]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
